import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import ReactECharts from "echarts-for-react";
import PainTimelineCard from "./PainTimelineCard";

const PainTimelineChart = forwardRef(function PainTimelineChart(props, ref) {
    const {
        getColorMapping: getColorMapping
    } = props;
    const chartRef = useRef(null);
    const [option, setOption] = useState({});
    const [tooltipData, setTooltipData] = useState(null);
    const initializeHighlight = useRef(true);
    const lastForcedHighlight = useRef(-1);
    const currentIndex = useRef(0)
    const diary = useRef(null)
    const cardVisible = useRef(true);


    const getMobileTooltipConfig = () => ({
        trigger: "axis",
        confine: true,
        appendToBody: false,
        symbolSize: 30,
        axisPointer: {
            type: "none"
        },
        formatter: function (params) {
            return "";
        }
    });

    const buildOption = (ctx = {}, minDate, maxDate) => {
        if (!diary.current?.entries?.length) return {};
        currentIndex.current = diary.current.entries.length - 1
        const colorMap = ctx.colorMap || getColorMapping(diary.current.attributes);
        return {
            legend: { data: [...(diary.current.attributes || []).map(a => a.name)] },
            tooltip: getMobileTooltipConfig(),
            xAxis: {
                type: "time",
            },
            boundaryGap: true,
            yAxis: {
                axisLabel: {
                    formatter: function (value) {
                        if (value < 0) return '';
                        return value;
                    },
                    rich: {
                    }
                }
            },
            grid: { top: "15%", left: "1%", right: "1%", bottom: "3%", containLabel: true },
            series: [

                ...(diary.current.attributes || []).map(attribute => ({
                    name: attribute.name,
                    type: "line",
                    symbolSize: 10,
                    stack: "pain",
                    showSymbol: false,
                    hoverAnimation: false,
                    areaStyle: {},
                    smooth: true,
                    color: colorMap.get(attribute.name),
                    emphasis: { focus: "none" },
                    silent: true,
                    data: diary.current.entries.map(entry => ({
                        value: [
                            entry.createdAt.replace("T", " ").replace("Z", ""),
                            entry.attributes.find(inner => inner.id === attribute.id)?.value || 0
                        ],
                        notes: entry.notes
                    })),
                }))
            ]
        };
    };

    useImperativeHandle(ref, () => ({
        update: (newDiary, ctx = {}, minDate, maxDate) => {
            diary.current = newDiary
            initializeHighlight.current = true
            const newOption = buildOption(ctx, minDate, maxDate);
            if (Object.keys(newOption).length) setOption(newOption);
            dispatchAction("highlight", newDiary.entries.length - 1)
        },
        getEChartsInstance: () => chartRef.current?.getEchartsInstance?.()
    }), []);

    const chartIsReady = (echartsInstance) => {
        if (!echartsInstance) {
            console.log("echartsInstance does not exist")
            return;
        }

        echartsInstance.on('highlight', function (params) {
            if (params?.batch?.[0]) {
                currentIndex.current = params.batch[0].dataIndex
                cardVisible.current = true
                setTooltipData({});
            }
            if (params?.mstephens || params?.[0]?.mstephens || params?.batch?.[0]?.mstephens) {
                return
            }
            if (lastForcedHighlight.current !== -1) {
                if (params.batch?.[0].dataIndex !== lastForcedHighlight.current) {
                    dispatchAction("downplay", lastForcedHighlight.current)
                }
            }
            setTooltipData({ "1": "2" })
            lastForcedHighlight.current = params?.batch?.[0]?.dataIndex
        });
        echartsInstance.on('downplay', function (params) {
            if (!params?.batch?.[0]?.mstephens && params?.batch?.length) {
                if (lastForcedHighlight.current !== currentIndex.current) {
                    dispatchAction("downplay", lastForcedHighlight.current)
                }
                lastForcedHighlight.current = currentIndex.current
                dispatchAction("highlight", lastForcedHighlight.current)
            }
        });
        echartsInstance.on('finished', function () {
            if (initializeHighlight.current) {
                initializeHighlight.current = false
                if (lastForcedHighlight.current != diary.current.entries.length - 1) {
                    dispatchAction("downplay", lastForcedHighlight.current)
                }
                dispatchAction("highlight", diary.current.entries.length - 1)
            }
        });

    }

    const closeCard = () => {
        cardVisible.current = false
        if (lastForcedHighlight.current != -1) {
            dispatchAction("downplay", lastForcedHighlight.current)
        }
        setTooltipData({ "1": "2" })
    }
    const leftClick = () => {
        if (currentIndex.current < 1) {
            return
        }
        dispatchAction("downplay", currentIndex.current)
        dispatchAction("highlight", currentIndex.current - 1)
    }

    const rightClick = () => {
        if (currentIndex.current > diary.current.entries.length - 2) {
            return
        }
        dispatchAction("downplay", currentIndex.current)
        dispatchAction("highlight", currentIndex.current + 1)
    }




    const dispatchAction = (action, index) => {
        const echartsInstance = chartRef.current?.getEchartsInstance();

        if (!echartsInstance) {
            console.log("EARLY RETURN ON DISPATCH ACTION")
            return;
        }
        if (!diary.current?.entries?.[index]?.attributes?.length) {
            console.log("skipping dispatch because ", index, " does not exist")
            return;
        }
        if (action === "highlight") {
            lastForcedHighlight.current = index
        }
        echartsInstance.dispatchAction({
            type: action,
            escapeConnect: true,
            mstephens: true,
            batch: diary.current.entries[index].attributes.map((batch, p) => {
                return {
                    "mstephens": true,
                    "seriesIndex": p,
                    "dataIndexInside": index,
                    "dataIndex": index,
                    "type": action,
                    "escapeConnect": true,
                    "notBlur": true,
                    "batch": null
                }
            })
        })
    }

    return (
        <div className="review-card" style={{ marginBottom: '1rem', overflow: 'hidden' }}>
            <h3 style={{ textAlign: 'center' }}>Pain History</h3>
            <div style={{ width: "100%", position: 'relative' }}>
                <div style={{ width: "100%", height: "350px", overflow: 'hidden' }}>
                    <ReactECharts
                        ref={chartRef}
                        option={option || {}}
                        style={{ height: "100%" }}
                        onChartReady={(instance) => chartIsReady(instance)}
                    />
                </div>
            </div>
            {diary.current?.entries?.length > 0 && (
                <PainTimelineCard
                    diary={diary.current}
                    currentIndex={currentIndex}
                    leftClick={leftClick}
                    rightClick={rightClick}
                    getColorMapping={getColorMapping}
                    closeCard={closeCard}
                    cardVisible={cardVisible.current}
                    isPain={true}
                    toast={props.toast}
                    loadDiary={props.loadDiary}
                    canEdit={props.canEdit}
                />
            )}
        </div>
    );
});

export default PainTimelineChart;
